import { useEffect, useRef } from 'react';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';

import {
  formatDays,
  inviteWaitDays,
  sendingOf,
  type AddressEntry,
} from './connection-actions';
import {
  connectVariables,
  PageGoneError,
  useConnectAddressMutation,
  useConnectMutation,
  useDisconnectMutation,
  type ConnectionSurface,
  type DisconnectSeen,
} from './connections.query';
import {
  pendingDeclines,
  type DeclineCallbacks,
} from './pending-declines.store';

import type { WikiLittlebirdAccount, WikiPerson } from '../people-wiki.types';

type Confirmation = {
  title: string;
  description: string;
  confirmText: string;
  cancelText: string;
  confirmVariant: 'default' | 'destructive';
};

const confirm = async (confirmation: Confirmation): Promise<boolean> =>
  Boolean(await ModalsManager.showResolvable(ConfirmDialog, confirmation));

// An invite sends an email in the user's name
const confirmInvite = (name: string, email: string) =>
  confirm({
    title: `Invite ${name} to Littlebird?`,
    description: `We'll email ${email} an invitation from you.`,
    confirmText: 'Send invite',
    cancelText: 'Cancel',
    confirmVariant: 'default',
  });

/** One question for the invites to several people, naming each person and the address that gets the email */
export const confirmInvites = (invites: { name: string; email: string }[]) => {
  const recipients = new Intl.ListFormat('en').format(
    invites.map(({ name, email }) => `${name} (${email})`)
  );
  const isSingle = invites.length === 1;
  return confirm({
    title: isSingle
      ? `Invite ${invites[0]?.name} to Littlebird?`
      : `Invite ${invites.length} people to Littlebird?`,
    description: `We'll email an invitation from you to ${recipients}.`,
    confirmText: isSingle ? 'Send invite' : 'Send invites',
    cancelText: 'Cancel',
    confirmVariant: 'default',
  });
};

// The title says what cancelling does, so the body tells what comes after it: the wait before the user can ask again
const confirmCancelRequest = (name: string, account: WikiLittlebirdAccount) => {
  const waitDays = inviteWaitDays(account);
  return confirm({
    title: `Cancel your request to ${name}?`,
    description: waitDays
      ? `You can ask again in ${formatDays(waitDays)}.`
      : 'You can ask again any time.',
    confirmText: 'Cancel request',
    cancelText: 'Keep',
    confirmVariant: 'destructive',
  });
};

const confirmRemove = (name: string) =>
  confirm({
    title: `Remove your connection with ${name}?`,
    description: 'You can connect again later.',
    confirmText: 'Remove',
    cancelText: 'Cancel',
    confirmVariant: 'destructive',
  });

/** Carries out what an address of a person offers when the user clicks it, as `connectionActions` decides. Accept and
 * Connect act at once, Invite and a request that is out ask first, and an invite that waits does nothing. `decline` is
 * the other answer to a request, which the user can undo. `onPageGone` is called when an action finds that the person's
 * page is gone, unless the component has unmounted by then. Each action says whether it
 * went through: it did not when the user turned a question down, and when the change failed, which the user was
 * told. */
export const useRunConnectionAction = (
  person: WikiPerson,
  surface: ConnectionSurface,
  { onPageGone }: { onPageGone?: () => void } = {}
) => {
  const { mutateAsync: connect, isPending: isConnecting } =
    useConnectMutation(person);
  const { mutateAsync: disconnect, isPending: isDisconnecting } =
    useDisconnectMutation(person);

  // A decline can be sent after the component has unmounted, and then there is no page left to leave
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const pageGone = () => {
    if (isMounted.current) onPageGone?.();
  };

  const attempt = async (change: Promise<unknown>): Promise<boolean> => {
    try {
      await change;
      return true;
    } catch (error) {
      if (error instanceof PageGoneError) pageGone();
      return false;
    }
  };

  const send = (entry: AddressEntry) =>
    attempt(connect(connectVariables(entry, surface)));

  const remove = ({ accountId }: WikiLittlebirdAccount, seen: DisconnectSeen) =>
    attempt(
      disconnect({ accountId, seen }).then((detail) => {
        // The page goes with its last account when it was made only for the request
        if (detail === null) pageGone();
      })
    );

  const click = async (entry: AddressEntry): Promise<boolean> => {
    const { email, account, state } = entry;
    switch (state) {
      case 'accept':
      case 'connect':
        return send(entry);
      case 'invite':
      case 'inviteAgain':
        return (await confirmInvite(person.name, email)) && send(entry);
      case 'requested':
        // A request that is out has an account on the page
        return (
          account !== null &&
          (await confirmCancelRequest(person.name, account)) &&
          remove(account, 'requested')
        );
      case 'connected':
        return (
          account !== null &&
          (await confirmRemove(person.name)) &&
          remove(account, 'connected')
        );
      case 'invited':
        return false;
    }
  };

  const decline = (
    { account }: AddressEntry,
    callbacks: DeclineCallbacks = {}
  ) => {
    if (!account) return;
    pendingDeclines.start({
      id: account.accountId,
      name: person.name,
      send: () => remove(account, 'incoming'),
      ...callbacks,
    });
  };

  return {
    click,
    decline,
    isPending: isConnecting || isDisconnecting,
  };
};

/** Carries out the Connect and the Invite that `connectionActions` offers for an address that no page holds yet. The
 * address gets a page when the user acts. */
export const useRunAddressAction = (surface: ConnectionSurface) => {
  const { mutateAsync: connect, isPending } = useConnectAddressMutation();

  const click = async ({ email, state }: AddressEntry): Promise<boolean> => {
    const sending = sendingOf(state);
    if (sending === 'invite' && !(await confirmInvite(email, email))) {
      return false;
    }
    try {
      await connect({ email, sending, surface });
      return true;
    } catch {
      // The mutation told the user what went wrong
      return false;
    }
  };

  return { click, isPending };
};
